/* BoatMat mobile sticky buy bar — 2026-09-06.
 *
 * Why this exists: on a phone the Add-to-Cart control sat roughly 2,700px below the size
 * selector — about seven thumb-swipes — and the running total and the purchase button could
 * never be on screen at the same time. A customer who picked a size had no way to see what it
 * cost and act on it without scrolling back and forth.
 *
 * What it deliberately does NOT do:
 *   - it never has its own price. It mirrors whatever the page's own total element says, so it
 *     cannot drift from the configurator, and the configurator's number is the server-priced
 *     one the cart will charge.
 *   - it never bypasses validation. It calls the page's own Add-to-Cart function, so every
 *     required-field check, every "Still to do" message and every busy state applies exactly
 *     as if the customer had scrolled down and tapped the real button.
 *   - it never appears on desktop, and it hides itself once the real purchase area is on
 *     screen, so it does not sit on top of the thing it is a shortcut to.
 *   - it reserves its own height at the bottom of the page (padding-bottom) so it can never
 *     cover the footer, a form field, or a cookie notice.
 *
 * Usage — each configurator calls:
 *     BMStickyBuy.init({
 *       totalEl:  '#hdlSummaryCart',      // element whose text contains the live total
 *       buyEl:    '#hdlAdd',              // the real purchase button (source of truth)
 *       needEl:   '#hdlNeed',             // optional: element listing what is still required
 *       anchorEl: '#hdlAdd',              // hide the bar when this scrolls into view
 *       label:    'Add to Cart'
 *     });
 */
(function (w, d) {
  'use strict';
  if (w.BMStickyBuy) return;

  var CSS = [
    /* The off-canvas mobile nav drawer (position:fixed, translateX(100%)) extends the document's
       scroll width by ~36px at 320px, so every configurator page could be side-scrolled on a
       phone. `clip` rather than `hidden` because `hidden` would turn the body into a scroll
       container and break position:sticky elsewhere on the page. Scoped to phone widths and to
       the pages that load this file. */
    '@media(max-width:759px){html,body{overflow-x:clip;max-width:100%}}',
    '#bmSticky{position:fixed;left:0;right:0;bottom:0;z-index:9500;display:none;',
    '  background:#ffffff;border-top:1px solid #dbe3ee;box-shadow:0 -6px 24px rgba(13,36,97,.14);',
    '  padding:8px 12px calc(8px + env(safe-area-inset-bottom,0px));',
    '  font-family:Barlow,system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;',
    '  -webkit-font-smoothing:antialiased}',
    /* nowrap: at 320px the row used to wrap and the bar grew to 171px tall, eating a third of
       the screen it was meant to keep clear. */
    '#bmSticky .bmsk-row{display:flex;flex-wrap:nowrap;align-items:center;gap:10px;max-width:720px;margin:0 auto}',
    '#bmSticky .bmsk-amt{flex:1 1 auto;min-width:0}',
    '#bmSticky .bmsk-lbl{display:block;font-size:.58rem;letter-spacing:.1em;text-transform:uppercase;color:#7d8ba0;font-weight:700;line-height:1.15;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
    '#bmSticky .bmsk-val{display:block;font-size:1.22rem;font-weight:800;color:#0d2461;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
    '#bmSticky .bmsk-btn{flex:0 1 auto;max-width:58%;min-height:46px;padding:0 16px;border:0;border-radius:11px;',
    '  background:#0d2461;color:#fff;font-size:.86rem;font-weight:800;letter-spacing:.03em;cursor:pointer;',
    '  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
    '#bmSticky .bmsk-btn[disabled]{background:#8fa0bb;cursor:not-allowed}',
    '#bmSticky .bmsk-need{max-width:720px;margin:0 auto 7px;font-size:.72rem;line-height:1.4;color:#8a5a00;',
    '  background:#fff5e0;border:1px solid #f3ddad;border-radius:9px;padding:7px 10px;',
    '  max-height:3.4em;overflow:hidden}',
    '@media(min-width:760px){#bmSticky{display:none !important}}'
  ].join('\n');

  function txt(el) { return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }

  /* Pull a money amount out of whatever the page's own total element renders. We read the
     page rather than recompute, so this bar can never show a different number. */
  function readTotal(el) {
    var t = txt(el);
    var m = t.match(/\$[\d,]+(?:\.\d{2})?/);
    return m ? m[0] : '';
  }

  var api = {
    _cfg: null, _bar: null, _btn: null, _val: null, _need: null, _busy: false,

    init: function (cfg) {
      if (this._bar || !cfg) return;
      this._cfg = cfg;

      var st = d.createElement('style');
      st.id = 'bmStickyCss';
      st.textContent = CSS;
      d.head.appendChild(st);

      var bar = d.createElement('div');
      bar.id = 'bmSticky';
      bar.setAttribute('role', 'region');
      bar.setAttribute('aria-label', 'Order summary');
      bar.innerHTML =
        '<div class="bmsk-need" id="bmSkNeed" hidden></div>' +
        '<div class="bmsk-row">' +
        '  <div class="bmsk-amt"><span class="bmsk-lbl">Total · before shipping &amp; tax</span>' +
        '  <span class="bmsk-val" id="bmSkVal">—</span></div>' +
        '  <button type="button" class="bmsk-btn" id="bmSkBtn"></button>' +
        '</div>';
      d.body.appendChild(bar);

      this._bar = bar;
      this._val = d.getElementById('bmSkVal');
      this._btn = d.getElementById('bmSkBtn');
      this._need = d.getElementById('bmSkNeed');
      this._btn.textContent = cfg.label || 'Add to Cart';

      var self = this;
      /* Delegate to the page's own button so every validation path runs unchanged. */
      this._btn.addEventListener('click', function () {
        var real = d.querySelector(self._cfg.buyEl);
        if (!real) return;
        real.scrollIntoView({ behavior: 'smooth', block: 'center' });
        real.click();
      });

      /* Mirror the page. A MutationObserver keeps us in step with any code path that
         changes the total or the button, including ones added later. */
      var sync = function () { self.sync(); };
      var mo = new MutationObserver(sync);
      [cfg.totalEl, cfg.buyEl, cfg.needEl].forEach(function (sel) {
        if (!sel) return;
        var el = d.querySelector(sel);
        if (el) mo.observe(el, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'class', 'hidden'] });
      });
      w.addEventListener('scroll', sync, { passive: true });
      w.addEventListener('resize', sync);
      d.addEventListener('click', function () { setTimeout(sync, 60); }, true);
      d.addEventListener('change', function () { setTimeout(sync, 60); }, true);
      d.addEventListener('input', function () { setTimeout(sync, 200); }, true);

      this.sync();
    },

    /* Keep the page's own bottom padding equal to the bar's height so the bar can never
       sit on top of the footer or the last form field. */
    _reserve: function (on) {
      var h = on && this._bar ? this._bar.offsetHeight : 0;
      d.body.style.paddingBottom = h ? h + 'px' : '';
    },

    sync: function () {
      if (!this._bar || this._busy) return;
      var cfg = this._cfg;
      var isPhone = w.matchMedia('(max-width: 759px)').matches;
      var real = d.querySelector(cfg.buyEl);
      var total = readTotal(d.querySelector(cfg.totalEl));

      /* Nothing configured yet, or the real button is already on screen: stay out of the way. */
      var anchor = d.querySelector(cfg.anchorEl || cfg.buyEl);
      var anchorVisible = false;
      if (anchor) {
        var r = anchor.getBoundingClientRect();
        anchorVisible = r.top < w.innerHeight - 40 && r.bottom > 0;
      }

      var show = isPhone && !!total && !anchorVisible;
      this._bar.style.display = show ? 'block' : 'none';
      this._reserve(show);
      if (!show) return;

      this._val.textContent = total;

      /* Mirror the real button's enabled state, and repeat whatever the page is already
         telling the customer is missing. Never enable something the page has disabled. */
      var disabled = !!(real && real.disabled);
      this._btn.disabled = disabled;
      this._btn.textContent = disabled ? 'Finish your design' : (cfg.label || 'Add to Cart');

      var needEl = cfg.needEl ? d.querySelector(cfg.needEl) : null;
      var needTxt = '';
      if (needEl && needEl.offsetParent !== null) needTxt = txt(needEl);
      if (needTxt) { this._need.textContent = needTxt; this._need.hidden = false; }
      else { this._need.hidden = true; }
    },

    busy: function (msg) {
      if (!this._btn) return;
      this._busy = true;
      this._btn.disabled = true;
      this._btn.textContent = msg || 'Working…';
    },
    idle: function () {
      this._busy = false;
      if (this._btn) this._btn.textContent = (this._cfg && this._cfg.label) || 'Add to Cart';
      this.sync();
    }
  };

  w.BMStickyBuy = api;
  w._bmSticky = api;   /* short alias used by the configurators' cart-push code */
})(window, document);
